import React, { useState, useCallback } from 'react';
import { format, addDays, subDays, differenceInDays, parseISO } from 'date-fns';
import { 
  Check, 
  X, 
  Edit3, 
  Download, 
  Filter,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  MoreHorizontal,
  Target,
  GraduationCap,
  Clock3,
  Users
} from 'lucide-react';

/**
 * TimelineSpreadsheet - Professional spreadsheet-style interface for timeline bounds management
 * Features: Inline editing, bulk operations, export functionality, sorting, filtering
 */
const TimelineSpreadsheet = ({ 
  positionedTemplates = [],
  timelineView,
  selectedCategories,
  anchorDatesCache,
  onUpdateTemplate,
  onBulkUpdate,
  TASK_CATEGORIES,
  APPLICATION_ROUNDS,
  getCategoryColor
}) => {
  // Spreadsheet state
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ field: 'title', direction: 'asc' });
  const [editingCell, setEditingCell] = useState(null); // { rowId, field }
  const [bulkUpdateMode, setBulkUpdateMode] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Available anchor options
  const ANCHOR_OPTIONS = [
    { value: 'timeline_baseline', label: 'Planning Start' },
    { value: 'senior_year_start', label: 'Senior Year Start' },
    { value: 'ed_deadline', label: 'ED Deadline (DEPRECATED)' },
    { value: 'ea_deadline', label: 'EA Deadline (DEPRECATED)' },
    { value: 'rd_deadline', label: 'RD Deadline (DEPRECATED)' },
    { value: 'earliest_application_deadline', label: 'Earliest Deadline' },
    { value: 'all_applications_completed', label: 'All Apps Completed' },
    { value: 'college_application_deadline', label: 'College Deadline (Smart)' },
    { value: 'national_decision_day', label: 'Decision Day' },
    { value: 'fafsa_opens', label: 'FAFSA Opens' },
    { value: 'css_deadline', label: 'CSS Deadline' }
  ];

  // Priority options
  const PRIORITY_OPTIONS = [
    { value: 'mandatory', label: 'Mandatory', color: 'bg-red-600 text-white' },
    { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'normal', label: 'Normal', color: 'bg-stone-100 text-slate-800' },
    { value: 'low', label: 'Low', color: 'bg-azure-100 text-azure-800' }
  ];

  // Get target date for a specific view context
  const getTargetDateForView = useCallback((template, viewType) => {
    try {
      let targetDateField;
      
      switch(viewType) {
        case 'max_rd': targetDateField = 'max_rd_target_date'; break;
        case 'min_rd': targetDateField = 'min_rd_target_date'; break;
        case 'max_ed': targetDateField = 'max_ed_target_date'; break;
        case 'min_ed': targetDateField = 'min_ed_target_date'; break;
        default: targetDateField = 'max_rd_target_date'; break;
      }
      
      const targetDate = template[targetDateField];
      if (!targetDate) return null;
      
      const dateObj = new Date(targetDate + 'T00:00:00');
      if (isNaN(dateObj.getTime())) return null;
      
      return dateObj;
    } catch (error) {
      console.error('Error getting target date:', error);
      return null;
    }
  }, []);

  // Update target date and calculate offset
  const updateTargetDate = useCallback(async (template, viewType, newDateStr) => {
    if (!newDateStr) return;

    console.log('\n🛠️ ===== TIMELINE BUILDER DEBUG =====');
    console.log('🛠️ Template:', template.title);
    console.log('🛠️ View Type:', viewType);
    console.log('🛠️ New Date String:', newDateStr);
    console.log('🛠️ Template Anchor ID:', template.anchor_id);

    try {
      const newDate = new Date(newDateStr + 'T00:00:00');
      if (isNaN(newDate.getTime())) {
        console.error('🛠️ ❌ Invalid date format:', newDateStr);
        return;
      }
      console.log('🛠️ Parsed Target Date:', newDate.toDateString());

      // Use static placeholder dates for visual planning based on MINIMAL timeline
      // (Latest start date: Sep 10, 2025)
      const staticPlaceholders = {
        'timeline_baseline': '2025-09-10',
        'timeline_baseline_before': '2025-09-10',
        'timeline_baseline_after': '2025-09-10',
        
        // Deprecated anchors (legacy support)
        'ed_deadline': '2025-11-01',
        'ed_deadline_before': '2025-11-01',
        'ed_deadline_after': '2025-11-01',
        'rd_deadline': '2026-01-01',
        'rd_deadline_before': '2026-01-01',
        'rd_deadline_after': '2026-01-01',
        
        // Core timeline anchors
        'senior_year_start': '2025-09-01',
        'senior_year_start_before': '2025-09-01',
        'senior_year_start_after': '2025-09-01',
        'graduation': '2026-06-15',
        'national_decision_day': '2026-05-01',
        'decision_day': '2026-05-01',
        'decision_day_before': '2026-05-01',
        'decision_day_after': '2026-05-01',
        
        // Financial aid anchors
        'fafsa_opens': '2025-10-01',
        'css_profile_opens': '2025-10-01',
        'finaid_deadline': '2026-02-15',
        
        // New anchor system - User specified fallback dates
        'earliest_application_deadline': '2025-10-29',
        'all_applications_completed': '2026-01-01',
        
        // Account creation and misc
        'account_creation': '2025-05-01',
        'common_app_opens': '2025-08-01',
        'task_completion': null // Dynamic, will need special handling
      };

      // Handle context-aware college deadline anchor
      let placeholderDate = staticPlaceholders[template.anchor_id];
      
      if (template.anchor_id === 'college_application_deadline') {
        // Context-aware logic: Nov 1 for Early, Jan 1 for Regular
        // Since we don't have college context in spreadsheet, use viewType as proxy
        if (viewType.includes('ed')) {
          placeholderDate = '2025-11-01'; // Early Decision deadline
          console.log('🛠️ Using Early Decision placeholder for college_application_deadline');
        } else {
          placeholderDate = '2026-01-01'; // Regular Decision deadline  
          console.log('🛠️ Using Regular Decision placeholder for college_application_deadline');
        }
      }
      
      if (!placeholderDate) {
        console.error('🛠️ ❌ No static placeholder for anchor:', template.anchor_id);
        console.log('🛠️ Available placeholders:', Object.keys(staticPlaceholders));
        
        // Fallback logic for unknown anchors
        console.log('🛠️ ⚠️ Using fallback logic for unknown anchor');
        
        // Provide sensible defaults based on anchor naming patterns
        if (template.anchor_id.includes('deadline')) {
          placeholderDate = viewType.includes('ed') ? '2025-11-01' : '2026-01-01';
          console.log('🛠️ Using deadline fallback:', placeholderDate);
        } else if (template.anchor_id.includes('completion') || template.anchor_id.includes('completed')) {
          placeholderDate = '2026-01-01';
          console.log('🛠️ Using completion fallback:', placeholderDate);
        } else if (template.anchor_id.includes('start') || template.anchor_id.includes('baseline')) {
          placeholderDate = '2025-09-01';
          console.log('🛠️ Using start/baseline fallback:', placeholderDate);
        } else {
          // Last resort: use RD deadline as universal fallback
          placeholderDate = '2026-01-01';
          console.log('🛠️ Using universal fallback (RD deadline):', placeholderDate);
        }
      }

      const anchor = new Date(placeholderDate + 'T00:00:00');
      console.log('🛠️ Static Placeholder Date:', anchor.toDateString(), `(${template.anchor_id})`);
      
      const daysDifference = differenceInDays(newDate, anchor);
      console.log('🛠️ Days Difference (target - anchor):', daysDifference);
      
      // Use signed offset (positive = after, negative = before)
      const signedOffsetDays = daysDifference;
      console.log('🛠️ ✅ Signed Offset:', signedOffsetDays, 'days', signedOffsetDays >= 0 ? '(after anchor)' : '(before anchor)');

      // Determine which offset fields to update
      const getOffsetDaysField = (view) => {
        const fieldMap = {
          'max_rd': 'max_rd_offset_days',
          'min_rd': 'min_rd_offset_days',
          'max_ed': 'max_ed_offset_days',
          'min_ed': 'min_ed_offset_days'
        };
        return fieldMap[view] || 'max_rd_offset_days';
      };

      const offsetDaysField = getOffsetDaysField(viewType);
      const targetDateField = `${viewType}_target_date`;
      console.log('🛠️ Database Field to Update:', offsetDaysField);

      // Show current template state for min/max validation
      console.log('🛠️ Current Template Offsets:');
      console.log('🛠️   max_rd_offset_days:', template.max_rd_offset_days);
      console.log('🛠️   min_rd_offset_days:', template.min_rd_offset_days);
      console.log('🛠️   max_ed_offset_days:', template.max_ed_offset_days);
      console.log('🛠️   min_ed_offset_days:', template.min_ed_offset_days);

      const updateData = {
        [offsetDaysField]: signedOffsetDays,
        [targetDateField]: newDateStr
      };

      console.log('🛠️ Update Data to Send:', updateData);
      
      // Predict potential constraint violations
      const currentTemplate = { ...template, ...updateData };
      console.log('🛠️ Template State After Update:');
      if (viewType.includes('rd')) {
        const maxRD = viewType === 'max_rd' ? signedOffsetDays : template.max_rd_offset_days;
        const minRD = viewType === 'min_rd' ? signedOffsetDays : template.min_rd_offset_days;
        console.log('🛠️   RD Range: min=', minRD, 'max=', maxRD);
        // Note: With signed offsets, min should be more negative (earlier) than max
        if (minRD !== null && maxRD !== null && minRD > maxRD) {
          console.log('🛠️ ⚠️  CONSTRAINT VIOLATION PREDICTED: min_rd_offset_days > max_rd_offset_days');
        }
      }
      if (viewType.includes('ed')) {
        const maxED = viewType === 'max_ed' ? signedOffsetDays : template.max_ed_offset_days;
        const minED = viewType === 'min_ed' ? signedOffsetDays : template.min_ed_offset_days;
        console.log('🛠️   ED Range: min=', minED, 'max=', maxED);
        // Note: With signed offsets, min should be more negative (earlier) than max
        if (minED !== null && maxED !== null && minED > maxED) {
          console.log('🛠️ ⚠️  CONSTRAINT VIOLATION PREDICTED: min_ed_offset_days > max_ed_offset_days');
        }
      }

      console.log('🛠️ Calling onUpdateTemplate...');
      await onUpdateTemplate(template.id, updateData);
      console.log('🛠️ ✅ Update successful!');
      console.log('🛠️ ===== END TIMELINE BUILDER DEBUG =====\n');
    } catch (error) {
      console.error('🛠️ ❌ Error updating target date:', error);
      console.log('🛠️ Error Details:', {
        message: error.message,
        templateId: template.id,
        templateTitle: template.title,
        viewType,
        newDateStr,
        anchorId: template.anchor_id
      });
      console.log('🛠️ ===== END TIMELINE BUILDER DEBUG =====\n');
    }
  }, [anchorDatesCache, onUpdateTemplate]);

  // Handle cell editing
  const handleCellEdit = useCallback((rowId, field, value) => {
    const template = positionedTemplates.find(t => t.id === rowId);
    if (!template) return;

    if (field.includes('_target_date')) {
      const viewType = field.replace('_target_date', '');
      updateTargetDate(template, viewType, value);
    } else {
      onUpdateTemplate(rowId, { [field]: value });
    }
    
    setEditingCell(null);
  }, [positionedTemplates, updateTargetDate, onUpdateTemplate]);

  // Sorting functionality
  const handleSort = useCallback((field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Sort templates based on current sort configuration
  const sortedTemplates = useCallback(() => {
    const sorted = [...positionedTemplates].sort((a, b) => {
      let aValue = a[sortConfig.field];
      let bValue = b[sortConfig.field];

      // Handle date sorting
      if (sortConfig.field.includes('target_date')) {
        const aDate = getTargetDateForView(a, sortConfig.field.replace('_target_date', ''));
        const bDate = getTargetDateForView(b, sortConfig.field.replace('_target_date', ''));
        
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        
        aValue = aDate.getTime();
        bValue = bDate.getTime();
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Handle numeric comparison
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [positionedTemplates, sortConfig, getTargetDateForView]);

  // Row selection handlers
  const handleRowSelect = useCallback((rowId, selected) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(rowId);
      } else {
        newSet.delete(rowId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback((selected) => {
    if (selected) {
      setSelectedRows(new Set(positionedTemplates.map(t => t.id)));
    } else {
      setSelectedRows(new Set());
    }
  }, [positionedTemplates]);

  // Export functionality
  const exportToCSV = useCallback(() => {
    const headers = [
      'Task Name',
      'Category', 
      'College Specific',
      'Visibility Settings',
      'Priority',
      'Anchor',
      'Max RD Date',
      'Max RD Offset',
      'Min RD Date',
      'Min RD Offset',
      'Max ED Date',
      'Max ED Offset',
      'Min ED Date',
      'Min ED Offset'
    ];

    const rows = sortedTemplates().map(template => [
      template.title,
      template.category,
      template.is_college_specific ? 'Yes' : 'No',
      template.is_college_specific ? [
        template.show_for_ed_schools && 'ED',
        template.show_for_ea_schools && 'EA',
        template.show_for_rd_schools && 'RD', 
        template.show_for_waitlisted_schools && 'WL',
        template.show_for_accepted_schools && 'AC',
        template.show_for_selected_school && 'SEL'
      ].filter(Boolean).join(' ') || 'None' : 'N/A',
      template.priority || 'normal',
      template.anchor_id,
      getTargetDateForView(template, 'max_rd')?.toISOString().split('T')[0] || '',
      template.max_rd_offset_days || 0,
      getTargetDateForView(template, 'min_rd')?.toISOString().split('T')[0] || '',
      template.min_rd_offset_days || 0,
      getTargetDateForView(template, 'max_ed')?.toISOString().split('T')[0] || '',
      template.max_ed_offset_days || 0,
      getTargetDateForView(template, 'min_ed')?.toISOString().split('T')[0] || '',
      template.min_ed_offset_days || 0
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `timeline-bounds-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportMenu(false);
  }, [sortedTemplates, getTargetDateForView]);

  // Show all four date columns for comprehensive timeline management
  const getVisibleDateColumns = () => {
    return [
      { key: 'max_rd_target_date', label: 'Max RD', viewType: 'max_rd', offsetField: 'max_rd_offset_days' },
      { key: 'min_rd_target_date', label: 'Min RD', viewType: 'min_rd', offsetField: 'min_rd_offset_days' },
      { key: 'max_ed_target_date', label: 'Max ED', viewType: 'max_ed', offsetField: 'max_ed_offset_days' },
      { key: 'min_ed_target_date', label: 'Min ED', viewType: 'min_ed', offsetField: 'min_ed_offset_days' }
    ];
  };

  const visibleDateColumns = getVisibleDateColumns();
  const templates = sortedTemplates();

  return (
    <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
      {/* Spreadsheet Header with Controls */}
      <div className="px-4 py-3 border-b border-stone-200 bg-stone-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h3 className="font-semibold text-slate-900">
              Timeline Bounds Management
            </h3>
            <div className="text-sm text-slate-600">
              {templates.length} tasks • {selectedRows.size} selected
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Bulk Operations Toggle */}
            {selectedRows.size > 0 && (
              <button
                onClick={() => setBulkUpdateMode(!bulkUpdateMode)}
                className="px-3 py-1.5 text-xs bg-azure-100 text-azure-700 rounded-md hover:bg-azure-200"
              >
                Bulk Edit ({selectedRows.size})
              </button>
            )}

            {/* Export Menu */}
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="flex items-center gap-2 px-3 py-1.5 text-xs bg-stone-100 text-slate-700 rounded-md hover:bg-stone-200"
              >
                <Download className="w-3 h-3" />
                Export
              </button>
              {showExportMenu && (
                <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg border border-stone-200 z-10">
                  <button
                    onClick={exportToCSV}
                    className="w-full px-3 py-2 text-xs text-left hover:bg-stone-50 rounded-md"
                  >
                    Export CSV
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Spreadsheet Table */}
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full text-xs" style={{ minWidth: '1400px' }}>
          <thead className="bg-stone-50 sticky top-0 z-10">
            <tr className="border-b border-stone-200">
              {/* Select All Checkbox */}
              <th className="px-2 py-3 text-left w-10">
                <input
                  type="checkbox"
                  className="rounded border-stone-300"
                  checked={selectedRows.size === templates.length && templates.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>

              {/* Task Name - Sortable & Sticky */}
              <th className="px-3 py-3 text-left font-medium text-slate-700 min-w-[180px] bg-stone-50 md:sticky md:left-0 md:z-20">
                <button
                  onClick={() => handleSort('title')}
                  className="flex items-center gap-1 hover:text-slate-900"
                >
                  Task Name
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>

              {/* Category */}
              <th className="px-3 py-3 text-left font-medium text-slate-700 w-20">
                Category
              </th>

              {/* College Specific */}
              <th className="px-3 py-3 text-center font-medium text-slate-700 w-16">
                College
              </th>

              {/* College Visibility */}
              <th className="px-3 py-3 text-center font-medium text-slate-700 w-20">
                Visibility
              </th>

              {/* Priority - Sortable */}
              <th className="px-3 py-3 text-left font-medium text-slate-700 w-20">
                <button
                  onClick={() => handleSort('priority')}
                  className="flex items-center gap-1 hover:text-slate-900"
                >
                  Priority
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>

              {/* Anchor */}
              <th className="px-3 py-3 text-left font-medium text-slate-700 w-28">
                Anchor
              </th>

              {/* Date Columns with Direction Controls */}
              {visibleDateColumns.map(column => (
                <th key={column.key} className="px-2 py-3 text-center font-medium text-slate-700 w-36">
                  <div className="flex flex-col items-center gap-1">
                    <button
                      onClick={() => handleSort(column.key)}
                      className="flex items-center gap-1 hover:text-slate-900 text-xs"
                    >
                      {column.label}
                      <ArrowUpDown className="w-3 h-3" />
                    </button>
                    <div className="text-[10px] text-stone-500">Date | Direction</div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-stone-200">
            {templates.map((template) => (
              <TimelineSpreadsheetRow
                key={template.id}
                template={template}
                isSelected={selectedRows.has(template.id)}
                onSelect={(selected) => handleRowSelect(template.id, selected)}
                onCellEdit={handleCellEdit}
                editingCell={editingCell}
                setEditingCell={setEditingCell}
                visibleDateColumns={visibleDateColumns}
                getTargetDateForView={getTargetDateForView}
                TASK_CATEGORIES={TASK_CATEGORIES}
                PRIORITY_OPTIONS={PRIORITY_OPTIONS}
                ANCHOR_OPTIONS={ANCHOR_OPTIONS}
                getCategoryColor={getCategoryColor}
              />
            ))}
          </tbody>
        </table>

        {templates.length === 0 && (
          <div className="text-center py-12 text-stone-500">
            <div className="text-sm">No tasks to display</div>
            <div className="text-xs mt-1">Check your filters or add some milestone templates</div>
          </div>
        )}
      </div>
    </div>
  );
};

// Individual spreadsheet row component
const TimelineSpreadsheetRow = ({ 
  template, 
  isSelected, 
  onSelect, 
  onCellEdit, 
  editingCell, 
  setEditingCell,
  visibleDateColumns,
  getTargetDateForView,
  TASK_CATEGORIES,
  PRIORITY_OPTIONS,
  ANCHOR_OPTIONS,
  getCategoryColor
}) => {
  const [editValue, setEditValue] = useState('');

  const handleEditStart = (field, currentValue) => {
    setEditingCell({ rowId: template.id, field });
    setEditValue(currentValue || '');
  };

  const handleEditSave = () => {
    if (editingCell) {
      onCellEdit(editingCell.rowId, editingCell.field, editValue);
    }
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const isEditing = (field) => {
    return editingCell && editingCell.rowId === template.id && editingCell.field === field;
  };

  return (
    <tr className={`hover:bg-stone-50 ${isSelected ? 'bg-azure-50' : ''}`}>
      {/* Selection Checkbox */}
      <td className="px-2 py-3">
        <input
          type="checkbox"
          className="rounded border-stone-300"
          checked={isSelected}
          onChange={(e) => onSelect(e.target.checked)}
        />
      </td>

      {/* Task Name - Sticky */}
      <td className="px-3 py-3 max-w-[125px] bg-white md:sticky md:left-0 md:z-10 border-r border-stone-200">
        <div className="flex items-center gap-2">
          <div 
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: getCategoryColor(template.category) }}
          />
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate">{template.title}</div>
            {template.description && (
              <div className="text-stone-500 text-xs truncate mt-0.5">
                {template.description}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Category */}
      <td className="px-3 py-3">
        <span 
          className="inline-flex items-center px-2 py-1 rounded-full text-[10px] min-w-[80px] text-center align-middle justify-center mx-auto font-medium"
          style={{
            backgroundColor: `${getCategoryColor(template.category)}15`,
            color: getCategoryColor(template.category)
          }}
        >
          {TASK_CATEGORIES[template.category]?.name || template.category}
        </span>
      </td>

      {/* College Specific */}
      <td className="px-3 py-3 text-center">
        <input
          type="checkbox"
          className="rounded border-stone-300"
          checked={template.is_college_specific || false}
          onChange={(e) => onCellEdit(template.id, 'is_college_specific', e.target.checked)}
        />
      </td>

      {/* College Visibility */}
      <td className="px-3 py-3 text-center">
        {template.is_college_specific ? (
          <div className="relative group">
            <button className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-colors">
              {[
                template.show_for_ed_schools && 'ED',
                template.show_for_ea_schools && 'EA', 
                template.show_for_rd_schools && 'RD',
                template.show_for_waitlisted_schools && 'WL',
                template.show_for_accepted_schools && 'AC',
                template.show_for_selected_school && 'SEL'
              ].filter(Boolean).join(' ') || 'None'}
            </button>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 p-2 bg-white border border-stone-200 rounded shadow-lg z-10 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity min-w-48">
              <div className="text-xs font-medium text-slate-700 mb-2">Show for college types:</div>
              <div className="space-y-1">
                {[
                  { key: 'show_for_ed_schools', label: 'Early Decision', abbr: 'ED' },
                  { key: 'show_for_ea_schools', label: 'Early Action', abbr: 'EA' },
                  { key: 'show_for_rd_schools', label: 'Regular Decision', abbr: 'RD' },
                  { key: 'show_for_waitlisted_schools', label: 'Waitlisted', abbr: 'WL' },
                  { key: 'show_for_accepted_schools', label: 'Accepted', abbr: 'AC' },
                  { key: 'show_for_selected_school', label: 'Selected School', abbr: 'SEL' }
                ].map(({ key, label, abbr }) => (
                  <label key={key} className="flex items-center space-x-2 text-xs">
                    <input
                      type="checkbox"
                      checked={template[key] || false}
                      onChange={(e) => onCellEdit(template.id, key, e.target.checked)}
                      className="rounded border-stone-300"
                    />
                    <span>{label} ({abbr})</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <span className="text-xs text-stone-400">N/A</span>
        )}
      </td>

      {/* Priority */}
      <td className="px-3 py-3">
        {isEditing('priority') ? (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleEditSave();
              if (e.key === 'Escape') handleEditCancel();
            }}
            className="w-full text-xs border border-stone-300 rounded px-1 py-1"
            autoFocus
          >
            {PRIORITY_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <button
            onClick={() => handleEditStart('priority', template.priority)}
            className="w-full text-left hover:bg-stone-100 rounded px-1 py-1"
          >
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              PRIORITY_OPTIONS.find(p => p.value === template.priority)?.color || 'bg-stone-100 text-slate-800'
            }`}>
              {template.priority || 'normal'}
            </span>
          </button>
        )}
      </td>

      {/* Anchor */}
      <td className="px-3 py-3">
        {isEditing('anchor_id') ? (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleEditSave();
              if (e.key === 'Escape') handleEditCancel();
            }}
            className="w-full text-xs border border-stone-300 rounded px-1 py-1"
            autoFocus
          >
            {ANCHOR_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <button
            onClick={() => handleEditStart('anchor_id', template.anchor_id)}
            className="w-full text-left hover:bg-stone-100 rounded px-1 py-1 text-xs"
          >
            {ANCHOR_OPTIONS.find(a => a.value === template.anchor_id)?.label || template.anchor_id}
          </button>
        )}
      </td>

      {/* Date Columns with Direction Controls */}
      {visibleDateColumns.map(column => {
        const currentDate = getTargetDateForView(template, column.viewType);
        const dateString = currentDate ? format(currentDate, 'yyyy-MM-dd') : '';
        const directionValue = template[column.directionField] || 'before';
        
        return (
          <td key={column.key} className="px-2 py-2">
            <div className="flex flex-col gap-1">
              {/* Date Input */}
              <div className="w-full">
                {isEditing(column.key) ? (
                  <input
                    type="date"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleEditSave}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleEditSave();
                      if (e.key === 'Escape') handleEditCancel();
                    }}
                    className="w-full text-xs border border-stone-300 rounded px-1 py-1"
                    autoFocus
                  />
                ) : (
                  <button
                    onClick={() => handleEditStart(column.key, dateString)}
                    className="w-full text-left hover:bg-stone-100 rounded px-1 py-1 text-xs"
                    title={`Click to edit ${column.label} date`}
                  >
                    {dateString ? format(currentDate, 'MMM dd') : '—'}
                  </button>
                )}
              </div>
              
              {/* Direction Dropdown */}
              <div className="w-full">
                {isEditing(column.directionField) ? (
                  <select
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleEditSave}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleEditSave();
                      if (e.key === 'Escape') handleEditCancel();
                    }}
                    className="w-full text-xs border border-stone-300 rounded px-1 py-1"
                    autoFocus
                  >
                    <option value="before">Before</option>
                    <option value="after">After</option>
                  </select>
                ) : (
                  <button
                    onClick={() => handleEditStart(column.directionField, directionValue)}
                    className="w-full text-left hover:bg-stone-100 rounded px-1 py-1 text-xs"
                    title={`Click to change direction (currently ${directionValue} anchor)`}
                  >
                    <span className={`inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium ${
                      directionValue === 'before' 
                        ? 'bg-azure-100 text-azure-700' 
                        : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {directionValue === 'before' ? '← Before' : 'After →'}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </td>
        );
      })}
    </tr>
  );
};

export default TimelineSpreadsheet;